import React from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector } from "react-redux";
import uploadIcon from "../../assets/upload.png";
import Thumbnail from "../../assets/Thumbnail.png";
import { darkTheme, lightTheme } from "../../theme/Theme";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import useThemeRender from "../../theme/useThemeRender";
import { setExternalFileDocument } from "../../slices/adminPageSlice.js";
import { useStepOneUpload } from "./useStepOneUpload";
import {
  renderProgressBar,
  ThumbnailFileInput,
  ThumbnailPreview,
} from "./uploadRenderHelpers";

const ExternalStepOneForm = ({
  externalData,
  setExternalData,
  setFileType,
}) => {
  const token = localStorage.getItem("auth_token");
  const { externalFileDocument } = useSelector((store) => store.adminpage);

  const {
    contentType,
    setContentType,
    isUploadingDocument,
    uploadPct,
    failedUpload,
    setFailedUpload,
    isUploadingThumbnail,
    thumbnailUploadPct,
    isDragging,
    setIsDragging,
    thumbnailInputRef,
    thumbnail,
    isDarkTheme,
    getThemeColor,
    dropzoneStyle,
    handlePreview,
    handleLinkChange,
    handleCancelUpload,
    handleRetryUpload,
    getRootProps,
    getInputProps,
    handleThumbnailUpload,
    handleDocumentDelete,
  } = useStepOneUpload({
    formData: externalData,
    setFormData: setExternalData,
    setFileType,
    fileDocument: externalFileDocument,
    setFileDocumentAction: setExternalFileDocument,
    // Preserved exactly as the original code dispatched it.
    fileUploadPageName: "internal",
    hasContentTypeSyncEffect: false,
    parseFileIdAsInt: false,
    thumbnailValidation: false,
    awaitDocumentDelete: true,
    logFileUploadedEffect: false,
  });

  const handleChange = (event) => {
    setContentType(event.target.value);
    setExternalData((prevData) => ({
      ...prevData,
      contentTypeId: event.target.value,
    }));
  };

  const insideCardBackgroundColorLayer3 = useThemeRender(
    "insideCardBackgroundColorLayer3"
  );

  const renderDropzone = (label) => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleThumbnailUpload({ target: { files: [file] } }); // Simulate input file event
      }
    };

    return (
      <Box>
        <FormLabel
          sx={{
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
          }}
        >
          {label}
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            border: isDragging ? "2px solid #005CBC" : "1px dashed #D7E5F8",
            background: isDragging
              ? "#E3F2FD"
              : getThemeColor(
                  lightTheme.insideCardBackgroundColorLayer3,
                  darkTheme.insideCardBackgroundColorLayer3
                ),
            padding: "24px",
            width: { xs: "100%", sm: "50%" },
            position: "relative",
            transition: "background 0.2s ease, border 0.2s ease",
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploadingThumbnail ? (
            renderProgressBar(thumbnailUploadPct)
          ) : thumbnail ? (
            <ThumbnailPreview
              thumbnail={thumbnail}
              thumbnailInputRef={thumbnailInputRef}
              isUploadingThumbnail={isUploadingThumbnail}
              handleThumbnailUpload={handleThumbnailUpload}
            />
          ) : (
            <>
              <img src={Thumbnail} alt="Thumbnail Icon" />
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: getThemeColor(
                    lightTheme.textColorLayer1,
                    darkTheme.textColorLayer1
                  ),
                }}
              >
                {isDragging
                  ? "Drop the file here"
                  : "Drag and drop your file here (only .jpg, .png)"}
              </Typography>
              <Button
                variant="text"
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                  border: "2px solid #005CBC",
                  fontWeight: 700,
                  py: 1,
                  px: 2,
                }}
                startIcon={<UploadOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
                disabled={isUploadingThumbnail}
              >
                Upload File
              </Button>
              <ThumbnailFileInput
                inputRef={thumbnailInputRef}
                onChange={handleThumbnailUpload}
                disabled={isUploadingThumbnail}
              />
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        background: getThemeColor(
          lightTheme.cardBackgroundColorLayer1,
          darkTheme.cardBackgroundColorLayer1
        ),
      }}
    >
      <Typography
        sx={{
          color: useThemeRender("textColorLayer1"),
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontFamily: "Inter",
          fontSize: "24px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "32px",
          mb: 4,
        }}
      >
        Step 1: Upload
      </Typography>

      <FormLabel
        sx={{
          color: getThemeColor(
            lightTheme.textColorLayer1,
            darkTheme.textColorLayer1
          ),
        }}
      >
        Content type
      </FormLabel>
      <RadioGroup value={contentType} onChange={handleChange} row>
        {/* {["DOCUMENT", "LINK"].map((type) => (
          <FormControlLabel
            key={type}
            value={type}
            control={
              <Radio
                sx={{
                  color: getThemeColor(
                    lightTheme.radioButtonLayer1,
                    darkTheme.radioButtonLayer1
                  ),
                }}
              />
            }
            label={type.charAt(0).toUpperCase() + type.slice(1)}
          />
        ))} */}
        <FormControlLabel
          value="DOCUMENT"
          control={
            <Radio
              sx={{
                color: getThemeColor(
                  lightTheme.radioButtonLayer1,
                  darkTheme.radioButtonLayer1
                ),
              }}
            />
          }
          label="Upload"
        />
        <FormControlLabel
          value="LINK"
          control={
            <Radio
              sx={{
                color: getThemeColor(
                  lightTheme.radioButtonLayer1,
                  darkTheme.radioButtonLayer1
                ),
              }}
            />
          }
          label="Link"
        />
      </RadioGroup>

      {contentType === "LINK" && (
        <TextField
          placeholder="Paste Link"
          sx={{ mt: 2, width: "100%" }}
          value={externalData.attachmentLinkUrl}
          onChange={handleLinkChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon />
              </InputAdornment>
            ),
          }}
        />
      )}

      {contentType === "DOCUMENT" && (
        <>
          <Box>
            {!externalFileDocument ? (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ width: { xs: "100%", sm: "50%" } }}
                >
                  <FormLabel
                    sx={{
                      color: getThemeColor(
                        lightTheme.textColorLayer1,
                        darkTheme.textColorLayer1
                      ),
                    }}
                  >
                    Document
                  </FormLabel>
                  <Box display="flex" justifyContent="space-between">
                    <FormLabel
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer4,
                          darkTheme.textColorLayer4
                        ),
                        fontSize: "14px",
                      }}
                    >
                      *Required
                    </FormLabel>
                  </Box>
                </Box>
                {failedUpload && !isUploadingDocument ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 2,
                      mb: 2,
                      width: { xs: "100%", sm: "50%" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ErrorOutlineOutlinedIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "error.main" }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: "14px" }}>
                          {failedUpload.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", color: "error.main" }}
                        >
                          Upload failed — you can retry
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AutorenewOutlinedIcon />}
                        sx={{ fontWeight: 700 }}
                        onClick={handleRetryUpload}
                      >
                        Retry
                      </Button>
                      <IconButton onClick={() => setFailedUpload(null)}>
                        <DeleteOutlineOutlinedIcon
                          fontSize="small"
                          color="primary"
                        />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                <Box
                  {...getRootProps()}
                  sx={{ mt: 2, mb: 2, ...dropzoneStyle }}
                >
                  {isUploadingDocument ? (
                    renderProgressBar(uploadPct, handleCancelUpload)
                  ) : (
                    <img src={uploadIcon} alt="Upload Icon" />
                  )}
                  <input {...getInputProps()} />
                  <Typography
                    sx={{
                      color: getThemeColor(
                        lightTheme.textColorLayer1,
                        darkTheme.textColorLayer1
                      ),
                    }}
                  >
                    Drag and drop your file here
                  </Typography>
                  <Button
                    variant="text"
                    sx={{
                      mt: 2,
                      borderRadius: "24px",
                      border: "2px solid #005CBC",
                      fontWeight: 700,
                      py: 1,
                      px: 2,
                    }}
                    startIcon={<UploadOutlinedIcon />}
                    disabled={isUploadingDocument}
                  >
                    Upload File
                  </Button>
                </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: insideCardBackgroundColorLayer3,
                  width: { xs: "100%", sm: "50%" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={handlePreview}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ marginRight: 1, color: "primary.main" }}
                  />
                  <Typography> {externalFileDocument?.name}</Typography>
                </Box>
                <IconButton
                  onClick={() =>
                    handleDocumentDelete(externalFileDocument?.fileId)
                  }
                >
                  <DeleteOutlineOutlinedIcon color="primary" />
                </IconButton>
              </Box>
            )}
          </Box>
          {renderDropzone("Thumbnail upload")}
        </>
      )}

      {contentType === "LINK" && renderDropzone("Thumbnail upload")}
    </Box>
  );
};

export default ExternalStepOneForm;
